import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Payroll, PayrollStatus } from '@/types/employee';
import { Payable, PayableType } from '@/types/finance/payable';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit, 
  startAfter,
  setDoc,
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import dayjs from 'dayjs';
import { loadMockData } from '@/mock';

// Helper function to filter mock data
function filterMockData<T extends { id: string; employee: { id: string; name: string }; status: string }>(
  data: T[],
  searchTerm?: string,
  status?: string,
  employeeId?: string
): T[] {
  let filteredData = data;
  
  if (searchTerm) {
    const lowercasedSearch = searchTerm.toLowerCase();
    filteredData = filteredData.filter(item => 
      item.employee.name.toLowerCase().includes(lowercasedSearch) ||
      item.id.toLowerCase().includes(lowercasedSearch)
    );
  }
  
  if (status) {
    filteredData = filteredData.filter(item => item.status === status);
  }
  
  if (employeeId) {
    filteredData = filteredData.filter(item => item.employee.id === employeeId);
  }
  
  return filteredData;
}

// Helper function to simulate pagination for mock data
function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const PAYROLL_COLLECTION = 'payrolls';
const PAYABLES_COLLECTION = 'payables';

// Types
export interface PayrollQueryParams {
  searchTerm?: string;
  status?: PayrollStatus;
  employeeId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

export interface CreatePayrollInput extends Omit<Payroll, 'id'> {
  employee: {
    id: string;
    name: string;
  };
  period: {
    startDate: Date;
    endDate: Date;
  };
  grossSalary: any;
  deductions: any[];
  netSalary: any;
  payable?: {
    id: string;
    name: string;
  };
}

export interface UpdatePayrollInput extends Partial<CreatePayrollInput> {
  id: string;
}

// Payroll API
export async function getPayrolls({
  searchTerm,
  status,
  employeeId,
  pageSize,
  page = 1,
  lastDoc,
  sortBy,
  sortOrder,
  firestoreUser
}: PayrollQueryParams): Promise<{ payrolls: Payroll[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock payrolls with filters', { searchTerm, status, employeeId, page, pageSize, lastDoc, sortBy, sortOrder });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockPayrolls = await loadMockData<Payroll[]>('payrolls', firestoreUser.language);
    const filteredData = filterMockData(mockPayrolls, searchTerm, status, employeeId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      payrolls: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }

  console.log('API Call: getPayrolls', { searchTerm, status, employeeId, pageSize, lastDoc, sortBy, sortOrder, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payrollsRef = collection(db, PAYROLL_COLLECTION);
    const filterConstraints = [where('workspace.id', '==', firestoreUser.workspace.id)];

    if (searchTerm) {
      filterConstraints.push(
        where('employee.name', '>=', searchTerm),
        where('employee.name', '<=', searchTerm + '\uf8ff')
      );
    }

    if (status) {
      filterConstraints.push(where('status', '==', status));
    }

    if (employeeId) {
      filterConstraints.push(where('employee.id', '==', employeeId));
    }

    let q = query(payrollsRef, ...filterConstraints);

    if (pageSize) {
      q = query(q, limit(pageSize));
    }

    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const payrolls = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        period: {
          startDate: formatFirestoreTimestamp(data.period?.startDate),
          endDate: formatFirestoreTimestamp(data.period?.endDate)
        }
      } as Payroll;
    });

    return {
      payrolls,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: payrolls.length
    };
  } catch (error) {
    console.error('Error fetching payrolls:', error);
    throw new Error('Failed to fetch payrolls');
  }
}

export async function getPayrollById(id: string, firestoreUser: User): Promise<Payroll | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock payroll with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockPayrolls = await loadMockData<Payroll[]>('payrolls', firestoreUser.language);
    const record = mockPayrolls.find(r => r.id === id) || null;
    return record;
  }

  console.log('API Call: getPayrollById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payrollsRef = collection(db, PAYROLL_COLLECTION);
    const q = query(
      payrollsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const payrollData = snapshot.docs[0].data();
    return {
      ...payrollData,
      id: snapshot.docs[0].id,
      period: {
        startDate: formatFirestoreTimestamp(payrollData.period?.startDate),
        endDate: formatFirestoreTimestamp(payrollData.period?.endDate)
      }
    } as Payroll;
  } catch (error) {
    console.error('Error fetching payroll:', error);
    throw new Error('Failed to fetch payroll');
  }
}

export async function addPayroll(
  payroll: CreatePayrollInput,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addPayroll', { payroll, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payrollData = addWorkspaceAndActivity(
      {
        ...payroll,
        period: {
          startDate: formatFirestoreTimestamp(payroll.period.startDate),
          endDate: formatFirestoreTimestamp(payroll.period.endDate)
        }
      },
      firestoreUser,
      'created'
    );

    const payrollsRef = collection(db, PAYROLL_COLLECTION);
    const newPayrollRef = doc(payrollsRef);
    
    await setDoc(newPayrollRef, removeUndefined({
      ...payrollData,
      id: newPayrollRef.id
    }));

    // Create activity record
    await createActivityRecord(
      'created',
      payrollData,
      firestoreUser
    );

    // If payroll status is pending, create a payable record
    if (payroll.status === 'pending') {
      const payableData: Omit<Payable, 'id'> = {
        name: `Payroll - ${payroll.employee.name} - ${dayjs(payroll.period.startDate).format('MMM YYYY')}`,
        type: 'payroll' as PayableType,
        status: 'pending',
        amount: payroll.netSalary,
        dueDate: payroll.period.endDate,
        description: `Payroll payment for ${payroll.employee.name} for period ${dayjs(payroll.period.startDate).format('MMM DD')} - ${dayjs(payroll.period.endDate).format('MMM DD, YYYY')}`,
        employee: payroll.employee,
        payroll: {
          id: newPayrollRef.id,
          name: `Payroll - ${payroll.employee.name}`
        },
        notes: payroll.notes,
        workspace: {
          id: firestoreUser.workspace.id,
          name: firestoreUser.workspace.name,
          slug: firestoreUser.workspace.slug
        },
        activityRecords: [{
          timestamp: new Date(),
          type: 'created',
          performedBy: {
            id: firestoreUser.id,
            displayName: firestoreUser.displayName || firestoreUser.email
          }
        }]
      };

      const payablesRef = collection(db, PAYABLES_COLLECTION);
      const newPayableRef = doc(payablesRef);
      
      await setDoc(newPayableRef, {
        ...payableData,
        id: newPayableRef.id
      });

      // Update payroll with payable reference
      await updateDoc(newPayrollRef, {
        payable: {
          id: newPayableRef.id,
          name: payableData.name
        }
      });
    }

    return newPayrollRef.id;
  } catch (error) {
    console.error('Error adding payroll:', error);
    throw new Error('Failed to add payroll');
  }
}

export async function updatePayroll(
  id: string,
  data: Partial<UpdatePayrollInput>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updatePayroll', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payrollsRef = collection(db, PAYROLL_COLLECTION);
    const q = query(
      payrollsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Payroll not found');
    }

    const payrollDoc = snapshot.docs[0];
    const updateData = removeUndefined({
      ...data,
      period: data.period ? {
        startDate: formatFirestoreTimestamp(data.period.startDate),
        endDate: formatFirestoreTimestamp(data.period.endDate)
      } : undefined
    });

    await updateDoc(payrollDoc.ref, updateData);

    // Create activity record
    await createActivityRecord(
      'updated',
      updateData,
      firestoreUser
    );
  } catch (error) {
    console.error('Error updating payroll:', error);
    throw new Error('Failed to update payroll');
  }
}

export async function deletePayroll(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deletePayroll', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const payrollsRef = collection(db, PAYROLL_COLLECTION);
    const q = query(
      payrollsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Payroll not found');
    }

    const payrollDoc = snapshot.docs[0];
    await deleteDoc(payrollDoc.ref);

    // Create activity record
    await createActivityRecord(
      'deleted',
      payrollDoc.data(),
      firestoreUser
    );
  } catch (error) {
    console.error('Error deleting payroll:', error);
    throw new Error('Failed to delete payroll');
  }
}

// Additional helper functions
export async function getPayrollsByStatus(status: PayrollStatus, firestoreUser: User): Promise<Payroll[]> {
  const { payrolls } = await getPayrolls({ status, firestoreUser, pageSize: 100 });
  return payrolls;
}

export async function getPayrollsByEmployee(employeeId: string, firestoreUser: User): Promise<Payroll[]> {
  const { payrolls } = await getPayrolls({ employeeId, firestoreUser, pageSize: 100 });
  return payrolls;
}

export async function getPayrollsByPeriod(startDate: Date, endDate: Date, firestoreUser: User): Promise<Payroll[]> {
  const { payrolls } = await getPayrolls({ firestoreUser, pageSize: 100 });
  return payrolls.filter(payroll => {
    const payrollStart = new Date(payroll.period.startDate);
    const payrollEnd = new Date(payroll.period.endDate);
    return payrollStart >= startDate && payrollEnd <= endDate;
  });
} 